<template>
  <div v-if="subscriptionsReceived && packagesReceived && userPasses.length">
    <slot name="title"/>
    <div
      v-if="$options.TABLE_IS_AVAILABLE && !isMobile"
      class="pt-3 pb-5 max-container-width"
    >
      <pass-table/>
    </div>
    <div
      v-else
      class="container-wrapper main-container"
    >
      <pass-component
        v-if="basicPassNeed"
        :item="basicPass"
        :is-recommended="false"
      >
        <template v-slot:button>
          <button-component
            title="get basic pass"
            @btn-click="selectBasicPass"
            :disabled="isBasicPassSelected"
          >
            {{ $t('Sign up') }}
          </button-component>
        </template>
      </pass-component>
      <pass-component
        v-for="pass in userPasses"
        :key="pass.id + pass.passType"
        :is-recommended="basicPassNeed"
        :item="pass"
      />
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex';
import GoogleAnalytics from '@/components/base-components/Analytics/GoogleAnalytics';
import {
  BASIC_PASS_FIELD_NAME,
  CUSTOM_FORM_ALIASES,
  ORDER_ACTIVITY_STATUS,
  ORDER_STATUS,
  ORDER_TYPE,
} from '@/const';
import {ALL_EVENTS, LOGIN} from '@/pages';
import VisaicV3Analytics from '@/components/base-components/Analytics/VisaicV3Analytics';
import {
  GET_CUSTOM_FORM_DATA,
  GET_ORDERS,
  GET_PACKAGES,
  GET_SUBSCRIPTIONS,
  SET_BASE_PASS_SELECTED,
} from '@/store/actionTypes';
import {mapCacheActions} from 'vuex-cache';
import ButtonComponent from '@/components/base-components/buttons/ButtonComponent.vue';
import {
  GET_BASIC_PASS_NEED,
  GET_BASIC_PASS_SELECTED,
  GET_PASSES_WITH_ACCESSES,
} from '@/store/getterTypes';
import componentsPaths from '@/componentsPaths';
import Vue from 'vue';

export default {
  name: 'PackagesNodes',
  components: {
    PassTable: () => {
      const path = Vue.prototype.$projectSettings?.pass?.tableComponent;
      return componentsPaths[path]?.()?.then((module) => module.default);
    },
    ButtonComponent,
    PassComponent: () => {
      const path = Vue.prototype.$projectSettings?.pass?.cardComponent || 'passCard';
      return componentsPaths[path]?.()?.then((module) => module.default);
    },
  },
  TABLE_IS_AVAILABLE: Vue.prototype.$projectSettings?.pass?.tableComponent,
  BASIC_PASS_FIELD_NAME,
  data() {
    return {
      packagesReceived: false,
      subscriptionsReceived: false,
      customFormAlias: CUSTOM_FORM_ALIASES.PASSES,
      basicPassProcessing: false,
      isMobile: false,
    };
  },
  inject: {
    watchFoViewChanges: 'watchFoViewChanges',
  },
  methods: {
    ...mapCacheActions({
      getPackagesCached: GET_PACKAGES,
      getSubscriptionsCached: GET_SUBSCRIPTIONS,
      getOrders: GET_ORDERS,
    }),
    ...mapActions({
      getCustomFormData: GET_CUSTOM_FORM_DATA,
      selectBasePass: SET_BASE_PASS_SELECTED,
    }),
    selectBasicPass() {
      if (!this.isLogged) {
        return this.$router.push(LOGIN);
      }
      if (!this.isBasicPassSelected) {
        this.basicPassProcessing = true;
        this.selectBasePass().then(() => {
          this.isBasicPassSelected && this.$router.push(ALL_EVENTS);
        }).finally(() => {
          this.basicPassProcessing = false;
        });
      }
    },
    sendAnalytics() {
      if (this.packagesReceived && this.subscriptionsReceived) {
        GoogleAnalytics.viewItemList({
          typedList: {
            [ORDER_TYPE.subscription]: this.subscriptions,
            [ORDER_TYPE.package]: this.packages,
          },
        });
        VisaicV3Analytics.event({
          event: 'view_item_list',
          availble_package: this.packages.map((puck) => puck.id),
          availble_subcription: this.subscriptions.map((subs) => subs.id),
        });
      }
    },
  },
  computed: {
    ...mapState({
      packages: (state) => state.payment.packagesList,
      subscriptions: (state) => state.payment.subscriptionsList,
    }),
    ...mapGetters({
      isLogged: 'isLogged',
      isBasicPassSelected: GET_BASIC_PASS_SELECTED,
      basicPassNeed: GET_BASIC_PASS_NEED,
      userPasses: GET_PASSES_WITH_ACCESSES,
    }),
    canTrack() {
      return this.packagesReceived && this.subscriptionsReceived;
    },
    basicPass() {
      return {
        name: this.$t('basicPass.title'),
        base_price: 0,
        description: {
          'time': 1694029653469,
          'blocks': [{
            'id': 'ooU3lGem9_',
            'type': 'list',
            'data': {
              'style': 'unordered',
              'items': [
                this.$t('basicPass.access'),
                this.$t('basicPass.freeWatch')],
            },
          }],
          'version': '2.26.5',
        },
      };
    },
  },
  watch: {
    canTrack() {
      this.sendAnalytics();
      !this.userPasses.length && this.$emit('no-passes');
    },
    isLogged() {
      this.isLogged && this.getOrders({
        status: ORDER_STATUS.paid,
        activity_status: ORDER_ACTIVITY_STATUS.active,
        no_limit: 1,
      });
    },
  },
  mounted() {
    this.getSubscriptionsCached().then(() => {
      this.subscriptionsReceived = true;
    });
    this.getPackagesCached().then(() => {
      this.packagesReceived = true;
    });
    this.isLogged && this.getOrders({
      status: ORDER_STATUS.paid,
      activity_status: ORDER_ACTIVITY_STATUS.active,
      no_limit: 1,
    });
    this.getCustomFormData(this.customFormAlias);
    const unsubscribeCb = this.watchFoViewChanges((isMobile) => {
      this.isMobile = isMobile;
    });
    this.$once('hook:beforeDestroy', unsubscribeCb);
  },
};
</script>

<style scoped lang="scss">
@import "src/assets/css/mixins";

.container-wrapper {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fill, minmax(23rem, 1fr));
  grid-auto-rows: 1fr;
  max-width: 71.25rem;
  @include media-max(xs) {
    grid-template-columns: 1fr;
  }
}
</style>
<style lang="scss">
@import "src/assets/css/purchaseCardSkin";
</style>
